import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FilterState } from '../types';
import { toDisplaySuburb, parseStyleSlug } from '../lib/slugs';
import { findLaunchedStateIndex, suburbSlugFromPathParts } from '../lib/stateRoutes';

// Radius cap matches the FilterModal slider max (1–20km); clamp URL-supplied
// values so a malformed ?radius= (NaN or absurd) can't silently zero the directory.
const MAX_RADIUS_KM = 20;
const parseRadius = (raw: string | null): number => {
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.min(n, MAX_RADIUS_KM) : 10;
};

interface UseDirectoryFiltersOptions {
    initialFilters?: FilterState | undefined;
    isVisible?: boolean;
}

/**
 * Extract the style slug from a directory path.
 * Handles /[launched-state]/:suburb/gyms/:styleSlug — returns the segment
 * after 'gyms'. Equivalent to the former parts.indexOf('nsw') lookup while
 * LAUNCHED_STATES === ['nsw'].
 */
function extractStyleSlugFromPath(pathname: string): string {
    const parts = pathname.split('/');
    const stateIdx = findLaunchedStateIndex(parts);
    if (stateIdx < 0) return '';
    const gymsIdx = parts.indexOf('gyms', stateIdx + 1);
    if (gymsIdx < 0 || gymsIdx + 1 >= parts.length) return '';
    return parts[gymsIdx + 1] || '';
}

export function useDirectoryFilters({ initialFilters, isVisible = true }: UseDirectoryFiltersOptions = {}) {
    const location = useLocation();

    // 1. Initialize filters from URL, State, or defaults
    const [filters, setFilters] = useState<FilterState>(() => {
        // Always use React Router's location for consistency between SSG and
        // client hydration — avoids hydration mismatch (React error #418).
        const pathname = location.pathname;
        const rawSearch = location.search;
        const searchParams = new URLSearchParams(rawSearch);

        // Parse suburb from path — shared shape-aware parse (a suburb exists only in
        // '/{state}/:suburb/gyms…'; a state DIRECTORY ROOT '/{state}/gyms' parses to
        // NO suburb, not a suburb named "Gyms" — the UI-57 review's FATAL).
        const pathParts = pathname.split('/');
        const suburbSlug = suburbSlugFromPathParts(pathParts);
        const suburb = suburbSlug ? toDisplaySuburb(suburbSlug) : '';

        // Parse primary style from path (/[launched-state]/:suburb/gyms/:styleSlug)
        const pathStyleSlug = extractStyleSlugFromPath(pathname);
        const pathStyle = pathStyleSlug ? parseStyleSlug(pathStyleSlug) : null;

        // Parse additional styles from query (?styles=BJJ,Judo) — multi-filter fallback
        const stylesParam = searchParams.get('styles');
        const queryStyles = stylesParam ? stylesParam.split(',').map(s => s.trim()).filter(Boolean) : [];

        // Path style is canonical; query styles are secondary
        const styles = pathStyle
            ? [pathStyle, ...queryStyles.filter(s => s !== pathStyle)]
            : queryStyles;

        // Parse Age Groups (?age=Adults,Teens)
        const ageParam = searchParams.get('age');
        const ageGroups = ageParam ? ageParam.split(',').map(s => s.trim()).filter(Boolean) : [];

        // Parse Experience Levels (?exp=Beginner,Intermediate) — multi-select
        const expParam = searchParams.get('exp');
        const experienceLevels = expParam ? expParam.split(',').map(s => s.trim()).filter(Boolean) : [];

        // Parse Radius (?radius=15)
        const radiusParam = searchParams.get('radius');
        const radius = parseRadius(radiusParam);
        const useRadius = radiusParam !== null;

        return {
            styles,
            ageGroups,
            experienceLevels,
            suburb,
            radius,
            useRadius,
            gymName: '',
            location_entry_id: null,
            location_source: null
        };
    });

    // 2. Helper: Generate a canonical search key
    const getCanonicalSearchKey = useCallback((suburb: string, radius: number, useRadius: boolean, entryId?: string | null) => {
        if (entryId) return `entryId_${entryId}_${radius}_${useRadius}`;
        const normalizedSuburb = suburb.toLowerCase().trim().replace(/[^\w\s]/g, '');
        return `${normalizedSuburb}_${radius}_${useRadius}`;
    }, []);

    const currentSearchKey = useMemo(() =>
        getCanonicalSearchKey(filters.suburb, filters.radius, filters.useRadius, filters.location_entry_id),
        [filters.suburb, filters.radius, filters.useRadius, filters.location_entry_id, getCanonicalSearchKey]);

    // 3. One-way hydration from initialFilters (e.g. from Landing Page)
    const hasHydratedRef = useRef<boolean>(false);
    useEffect(() => {
        if (!initialFilters || hasHydratedRef.current) return;

        if (!filters.suburb && initialFilters.suburb) {
            hasHydratedRef.current = true;
            setFilters(initialFilters);
        }
    }, [initialFilters, filters.suburb]);

    // 4. Sync filters from URL changes (browser back/forward)
    useEffect(() => {
        if (!isVisible) return;

        const pathParts = location.pathname.split('/');
        const suburbSlug = suburbSlugFromPathParts(pathParts); // shape-aware: root/hub paths parse to ''
        const parsedSuburb = suburbSlug ? toDisplaySuburb(suburbSlug) : '';

        if (!parsedSuburb && filters.suburb && !location.state?.filters) {
            // Reset if we moved away from a location path
            setFilters({
                styles: [],
                ageGroups: [],
                experienceLevels: [],
                suburb: '',
                radius: 10,
                useRadius: false,
                gymName: '',
                location_entry_id: null,
                location_source: null
            });
            return;
        }

        // Parse style from path (/nsw/:suburb/gyms/:styleSlug)
        const pathStyleSlug = extractStyleSlugFromPath(location.pathname);
        const pathStyle = pathStyleSlug ? parseStyleSlug(pathStyleSlug) : null;

        // Parse additional styles from query params
        const searchParams = new URLSearchParams(location.search);
        const stylesParam = searchParams.get('styles');
        const queryStyles = stylesParam ? stylesParam.split(',').map(s => s.trim()).filter(Boolean) : [];
        const styles = pathStyle
            ? [pathStyle, ...queryStyles.filter(s => s !== pathStyle)]
            : queryStyles;

        const ageParam = searchParams.get('age');
        const ageGroups = ageParam ? ageParam.split(',').map(s => s.trim()).filter(Boolean) : [];

        const expParam = searchParams.get('exp');
        const experienceLevels = expParam ? expParam.split(',').map(s => s.trim()).filter(Boolean) : [];

        const radiusParam = searchParams.get('radius');
        const radius = parseRadius(radiusParam);
        const useRadius = radiusParam !== null;

        const needsUpdate =
            (parsedSuburb !== filters.suburb && !location.state?.filters) ||
            (JSON.stringify([...styles].sort()) !== JSON.stringify([...filters.styles].sort()) && !location.state?.filters) ||
            (JSON.stringify([...ageGroups].sort()) !== JSON.stringify([...filters.ageGroups].sort()) && !location.state?.filters) ||
            (JSON.stringify([...experienceLevels].sort()) !== JSON.stringify([...filters.experienceLevels].sort()) && !location.state?.filters) ||
            (radius !== filters.radius && !location.state?.filters) ||
            (useRadius !== filters.useRadius && !location.state?.filters);

        if (needsUpdate) {
            setFilters(prev => ({
                ...prev,
                suburb: parsedSuburb,
                styles,
                ageGroups,
                experienceLevels,
                radius,
                useRadius,
                location_entry_id: location.state?.filters?.location_entry_id || null,
                location_source: location.state?.filters?.location_source || null
            }));
        }
    }, [location.pathname, location.search, isVisible, filters.suburb]);

    // 5. Sync filters from navigation state (hot reload for Saved Searches)
    const lastProcessedFiltersRef = useRef<string>('');
    const lastEntryKeyRef = useRef<string>(location.key);
    useEffect(() => {
        // UI-52 (operator-ruled 2026-08-11): the dedupe's LIFETIME was wrong, not its
        // key. DirectoryPage never unmounts, so this ref outlived the navigation it
        // belonged to and suppressed a LATER entry's filters as "already applied".
        //
        // The failing sequence, all three steps in-page:
        //   1. apply a filter    → REPLACE with state.filters=F1; ref records F1
        //   2. load a saved search from the DIRECTORY's own dropdown → PUSH with NO
        //      router state, so the early return below fired and the ref still held F1
        //      while effect 4 moved `filters` to F2 from the URL
        //   3. press Back        → POP restores step 1's entry (state.filters=F1); the
        //      ref still equals F1, the dedupe hit, and NOTHING was applied — leaving
        //      the chips/results on F2 under an address describing F1, permanently
        //      (effect 4 is inert whenever an entry carries state.filters).
        //
        // ⚠️ THE CLEAR MUST SIT ABOVE THE EARLY RETURN. Step 2 is precisely a
        // navigation carrying NO state.filters; if the clear were below the guard it
        // would never run on the divergence step and the bug would survive verbatim.
        //
        // ⚠️ Why not re-KEY the ref on location.key, as the register prescribed: a POP
        // restores the entry's ORIGINAL key (@remix-run/router persists it into
        // window.history.state), so on step 3 the key equals the one recorded at step 1
        // and the dedupe hits identically. That fix is a no-op on the only path that
        // fails. Scoping the ref's LIFETIME to the entry is what actually closes it.
        //
        // Within a single entry the dedupe still applies, which is the job it does do:
        // an isVisible toggle (map/list, off-route and back) must not re-apply and wipe
        // filter fields the URL cannot carry — a typed gymName above all.
        if (lastEntryKeyRef.current !== location.key) {
            lastEntryKeyRef.current = location.key;
            lastProcessedFiltersRef.current = '';
        }

        if (!isVisible || !location.state?.filters) return;

        const incomingFilters = location.state.filters as FilterState;
        const incomingKey = JSON.stringify(incomingFilters);

        if (lastProcessedFiltersRef.current !== incomingKey) {
            lastProcessedFiltersRef.current = incomingKey;
            setFilters(incomingFilters);
        }
    }, [location.key, location.state, isVisible]);

    return {
        filters,
        setFilters,
        currentSearchKey,
        getCanonicalSearchKey
    };
}
